import React from 'react';
import { Skeleton, SparkIcon, TooltipTrigger } from '@spark';
import '../styles/FieldCard.css';

/** Figma 581:59009 name-row skeleton widths, cycled per card. */
const NAME_SKELETON_WIDTHS = [51, 69, 73, 46, 47];

/**
 * Field card (Figma 581:59009): name + sample value, type icon, and a
 * mapped / insert / primary state icon. Blue accent for dataset columns,
 * green for custom fields (e.g. the primary model input).
 *
 * Shared by the Map-prompt-template "Available fields" panel and the Model
 * Configuration "Additional fields" rail. Class names keep the original
 * `cw-pm-` prefix from the prompt mapper, where the card design originated.
 */
const FieldCard = ({
    name,
    value,
    typeIconSlug,
    occurrences,
    isMapped,
    isPrimary,
    variant = 'column',
    loading = false,
    /** Shuffle: name and mapped state stay; only the value line reloads. */
    valueLoading = false,
    skeletonIndex = 0,
    interactive,
    onClick,
    onSetPrimary,
    /** Overrides the default tooltip ("insert at the cursor" / "unmap"). */
    tooltip: tooltipOverride,
}) => {
    const isCustom = variant === 'custom';
    // Only the primary model input shows the pin; primary is always mapped,
    // so the check stays beside it. Figma's loading cards show check only —
    // pin is hidden until content lands.
    const showPin = Boolean(isPrimary && isMapped && !loading);
    // Mapped non-primary cards offer a hover pin (unfilled, same spot) that
    // promotes the field to primary. Only mapped fields qualify — same rule
    // as the Primary model input dropdown. Picking a primary doesn't edit the
    // template, so the pin stays available on both tabs (not just Edit
    // Template) — the panel withholds onSetPrimary while content loads.
    const showSetPrimary = Boolean(!showPin && isMapped && !loading && onSetPrimary);
    const tooltip = !interactive
        ? null
        : tooltipOverride ??
          (isMapped
              ? 'Click to unmap and restore the original text'
              : 'Click to insert at the cursor');
    const card = (
        <button
            type="button"
            className={[
                'cw-pm-field-card',
                isMapped ? 'cw-pm-field-card--mapped' : '',
                isCustom ? 'cw-pm-field-card--custom' : '',
                interactive ? '' : 'cw-pm-field-card--static',
            ]
                .filter(Boolean)
                .join(' ')}
            aria-label={name}
            aria-busy={loading || valueLoading || undefined}
            onClick={interactive ? onClick : undefined}
        >
            <span className="cw-pm-field-card__type" data-type-icon={typeIconSlug}>
                <SparkIcon slug={typeIconSlug} size="small" />
            </span>
            {loading ? (
                <span className="cw-pm-field-card__main cw-pm-field-card__main--skeleton cw-pm-skeleton">
                    <Skeleton
                        shapeType="rectangle"
                        width={NAME_SKELETON_WIDTHS[skeletonIndex % NAME_SKELETON_WIDTHS.length]}
                        height={15}
                    />
                    <Skeleton shapeType="rectangle" width={163} height={15} />
                </span>
            ) : (
                <span className="cw-pm-field-card__main">
                    <span className="cw-pm-field-card__name">
                        {name}
                        {isMapped && occurrences ? (
                            <span className="cw-pm-field-card__count"> ({occurrences})</span>
                        ) : null}
                    </span>
                    {valueLoading ? (
                        <span className="cw-pm-field-card__value cw-pm-field-card__value--skeleton cw-pm-skeleton">
                            <Skeleton shapeType="rectangle" width={163} height={15} />
                        </span>
                    ) : (
                        // Always rendered (empty stays blank): every card keeps
                        // the two-line height, so the Custom fields and Dataset
                        // columns sections line up.
                        <span className="cw-pm-field-card__value">{value || ''}</span>
                    )}
                </span>
            )}
            <span className="cw-pm-field-card__states">
                {showPin ? (
                    <span className="cw-pm-field-card__state cw-pm-field-card__state--pin">
                        <SparkIcon slug="pin-filled" size="small" />
                    </span>
                ) : null}
                {showSetPrimary ? (
                    <TooltipTrigger
                        text="Set as primary model input"
                        className="cw-pm-field-card__state cw-pm-field-card__state--pin-set"
                    >
                        <span
                            role="button"
                            tabIndex={0}
                            aria-label={`Set ${name} as primary model input`}
                            className="cw-pm-field-card__pin-set-btn"
                            onClick={(event) => {
                                // Don't let the click bubble into the card's
                                // map/unmap toggle.
                                event.stopPropagation();
                                onSetPrimary();
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onSetPrimary();
                                }
                            }}
                        >
                            <SparkIcon slug="pin" size="small" />
                        </span>
                    </TooltipTrigger>
                ) : null}
                <span
                    className={[
                        'cw-pm-field-card__state',
                        isMapped ? 'cw-pm-field-card__state--check' : 'cw-pm-field-card__state--plus',
                    ].join(' ')}
                >
                    <SparkIcon slug={isMapped ? 'check' : 'plus'} size="small" />
                </span>
            </span>
        </button>
    );
    // The wrapper class pins the tooltip span to the panel width — Spark's
    // default (inline-flex, content-sized) would let a long nowrap value line
    // stretch the card past the panel instead of truncating with an ellipsis.
    return tooltip ? (
        <TooltipTrigger text={tooltip} className="cw-pm-field-card-tip">
            {card}
        </TooltipTrigger>
    ) : (
        card
    );
};

export default FieldCard;
